import React, {useMemo} from 'react';
import {useSelector} from 'react-redux';

import {NotesSection} from 'src/components/notes-section';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {useDateTime} from 'src/hooks/date-time/useDateTime';
import {selectAccountLicense} from 'src/modules/profile/account/tabs/license-and-features/services/account-license-features-slice';

import {useLicenseNotes} from './useLicenseNotes';

type Props = {
    licenseId: number,
    accountId: number,
};

export const LicenseNotes: React.FC<Props> = ({licenseId, accountId}) => {
    const license = useSelector(selectAccountLicense);
    const {isFutureDate} = useDateTime();

    const {
        isLoading: isLicenseNotesLoading,
        loadingError: licenseNotesLoadingError,
        isChanged: isLicenseNotesChanged,

        data: licenseNotes,
        setData: setLicenseNotes,

        saveChanges: saveLicenseNotes,
        discardChanges: discardLicenseNotes,
    } = useLicenseNotes({licenseId});

    const {canEditLicenseNotes} = useAdminAccessRight();

    const disabled = useMemo(() => {
        if (!license) {
            return true;
        }

        return (!isFutureDate(license.endDate) && !license.isActive);
    }, [license, isFutureDate]);

    const canEdit = useMemo(() => canEditLicenseNotes(accountId), [accountId, canEditLicenseNotes]);

    return (
        <NotesSection
            headline="License Notes"
            disabled={disabled}
            isLoading={isLicenseNotesLoading}
            loadingError={licenseNotesLoadingError}
            isNotesChanged={isLicenseNotesChanged}

            canEdit={canEdit}
            notes={licenseNotes?.value || ''}
            chunk="license-notes"

            onChange={setLicenseNotes}
            onSave={saveLicenseNotes}
            onDiscard={discardLicenseNotes}
        />
    );
};
